import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CalloutPin, type TabCalloutProps } from "./CalloutPin";
import {
  INDEX_CALLOUTS,
  INDEX_COMPARE,
  INDEX_REASONS,
  INDEXED_TREND,
  INDEXING,
  SPARKLINE_COLOR,
  STATUS_GOOD,
  SURFACE,
} from "./gscData";
import {
  formatNumber,
  GRID_STROKE,
  TOOLTIP_CONTENT_STYLE,
  TOOLTIP_ITEM_STYLE,
  TOOLTIP_LABEL_STYLE,
} from "./chartTheme";

const NEUTRAL_BAR = "rgba(255,255,255,0.28)";

function StatBlock({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: string;
}) {
  return (
    <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-5">
      <span
        className="absolute inset-x-0 top-0 h-1 rounded-t-2xl"
        style={{ background: accent ?? NEUTRAL_BAR }}
      />
      <p className="text-xl text-white/60">{label}</p>
      <p className="mt-1 font-sans text-6xl font-semibold text-white">
        {value.toLocaleString("en-US")}
      </p>
    </div>
  );
}

export function IndexingTab({ openId, visited, onOpen }: TabCalloutProps) {
  const pinProps = { openId, visited, onOpen };
  const last = INDEXED_TREND[INDEXED_TREND.length - 1];

  return (
    <div className="grid h-full grid-cols-[1.1fr_1fr] gap-4">
      {/* Left column */}
      <div className="flex min-h-0 flex-col gap-4">
        <div className="grid shrink-0 grid-cols-2 gap-4">
          <StatBlock label="Indexed" value={INDEXING.indexed} accent={STATUS_GOOD} />
          <div className="relative">
            <StatBlock label="Not indexed" value={INDEXING.notIndexed} />
            {/* ③ Judge the reasons, not the total */}
            <CalloutPin
              callout={INDEX_CALLOUTS.reasons}
              className="absolute -right-3 -top-3"
              {...pinProps}
            />
          </div>
        </div>

        {/* Two-bar comparison */}
        <div className="shrink-0 rounded-2xl border border-white/10 bg-white/[0.03] p-4 pb-2">
          <p className="pl-1 text-xl text-white/60">Pages · indexed vs not indexed</p>
          <ResponsiveContainer width="100%" height={104}>
            <BarChart
              data={INDEX_COMPARE}
              layout="vertical"
              margin={{ top: 8, right: 82, bottom: 4, left: 0 }}
            >
              <XAxis type="number" hide domain={[0, 9000]} />
              <YAxis
                type="category"
                dataKey="name"
                width={132}
                tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 18 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={TOOLTIP_CONTENT_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
                itemStyle={TOOLTIP_ITEM_STYLE}
                cursor={{ fill: "rgba(255,255,255,0.04)" }}
                formatter={(value) => formatNumber(value)}
              />
              <Bar dataKey="value" name="Pages" barSize={20} radius={[0, 4, 4, 0]}>
                <Cell fill={STATUS_GOOD} />
                <Cell fill={NEUTRAL_BAR} />
                <LabelList
                  dataKey="label"
                  position="right"
                  fill="rgba(255,255,255,0.85)"
                  fontSize={18}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Indexed-pages sparkline — single series, endpoint dot + value label */}
        <div className="relative flex min-h-0 flex-1 flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <p className="pl-1 text-xl text-white/60">Indexed pages over time · weekly</p>
          <div className="min-h-0 flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={INDEXED_TREND} margin={{ top: 12, right: 86, bottom: 8, left: 8 }}>
                <CartesianGrid stroke={GRID_STROKE} vertical={false} />
                <XAxis dataKey="week" hide />
                <YAxis hide domain={[7000, 8700]} />
                <Tooltip
                  contentStyle={TOOLTIP_CONTENT_STYLE}
                  labelStyle={TOOLTIP_LABEL_STYLE}
                  itemStyle={TOOLTIP_ITEM_STYLE}
                  cursor={{ stroke: "rgba(255,255,255,0.2)", strokeWidth: 1 }}
                  formatter={(value) => formatNumber(value)}
                />
                <Line
                  type="monotone"
                  dataKey="indexed"
                  name="Indexed pages"
                  stroke={SPARKLINE_COLOR}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 5, stroke: SURFACE, strokeWidth: 2 }}
                />
                {last && (
                  <ReferenceDot
                    x={last.week}
                    y={last.indexed}
                    r={5}
                    fill={SPARKLINE_COLOR}
                    stroke={SURFACE}
                    strokeWidth={2}
                    label={{
                      value: last.indexed.toLocaleString("en-US"),
                      position: "right",
                      fill: "rgba(255,255,255,0.9)",
                      fontSize: 18,
                      offset: 10,
                    }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          {/* ② pinned on the mid-series dip — the fire alarm */}
          <CalloutPin
            callout={INDEX_CALLOUTS.dip}
            className="absolute left-[48%] top-[56%]"
            {...pinProps}
          />
        </div>
      </div>

      {/* Right column — reasons table */}
      <div className="min-h-0 rounded-2xl border border-white/10 bg-white/[0.03]">
        <p className="border-b border-white/[0.06] px-5 py-3.5 text-xl uppercase tracking-wider text-white/50">
          Why pages aren't indexed
        </p>
        <table className="w-full">
          <thead>
            <tr className="text-xl uppercase tracking-wider text-white/50">
              <th className="px-5 py-3 text-left font-medium">Reason</th>
              <th className="px-5 py-3 text-right font-medium">Pages</th>
            </tr>
          </thead>
          <tbody>
            {INDEX_REASONS.map((r) => {
              const isNoindex = r.reason.includes("noindex");
              return (
                <tr
                  key={r.reason}
                  className="border-t border-white/[0.06] transition-colors hover:bg-white/5"
                >
                  <td className="relative px-5 py-4 pr-12 text-xl text-white/90">
                    {r.reason}
                    {isNoindex && (
                      // ① accidental noindex on an important template → fix first
                      <CalloutPin
                        callout={INDEX_CALLOUTS.templates}
                        className="absolute right-1 top-1/2 -translate-y-1/2"
                        {...pinProps}
                      />
                    )}
                  </td>
                  <td className="px-5 py-4 text-right text-xl tabular-nums text-white/85">
                    {r.pages.toLocaleString("en-US")}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
